import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  styled,
  alpha,
  useTheme,
  TextField,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  InputAdornment,
  Collapse,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Card,
  CardContent,
  MenuItem,
  Tooltip,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import BadgeIcon from "@mui/icons-material/Badge";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AssessmentIcon from "@mui/icons-material/Assessment";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import ReplayIcon from "@mui/icons-material/Replay";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import NotificationSnackbar from "./NotificationBar";

const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: "600px",
  overflowX: "auto",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "12px",
  "&::-webkit-scrollbar": { height: "8px", width: "8px" },
  "&::-webkit-scrollbar-track": {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha(theme.palette.primary.main, 0.25),
    borderRadius: "4px",
    "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.4) },
  },
}));

function ApplicantSession() {
  const theme = useTheme();
  const PRIMARY_COLOR = "#0a2e52";
  const SECONDARY_COLOR = "#1565C0";
  const DEFAULT_COLOR = "#FFFFFF";

  const [searchTerm, setSearchTerm] = useState("");
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showData, setShowData] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const actionMenuOpen = Boolean(anchorEl);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [sessionToView, setSessionToView] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const handleSnackbarClose = () => setSnackbarOpen(false);

  // SEARCH CALL (POST /api/support/applicant-application-sessions)
  const handleSearch = async () => {
    const term = searchTerm.trim();
    if (!term) {
      setShowData(false);
      setSessions([]);
      return;
    }
    try {
      setLoading(true);
      setError(null);

      const res = await fetch("/api/support/applicant-application-sessions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ applicationId: term }),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        const msg = (data && data.message) || `HTTP ${res.status}`;
        setError(msg);
        setShowData(true);
        setSessions([]);
        return;
      }

      const list = Array.isArray(data) ? data : data ? [data] : [];
      if (list.length > 0) {
        setSessions(list);
        setShowData(true);
        setError(null);
      } else {
        setSessions([]);
        setShowData(true);
        setError("No session records found for this Application ID.");
      }
    } catch (err) {
      console.error("Search failed:", err);
      setError("Search failed. Please try again.");
      setShowData(true);
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setError(null);
    setShowData(false);
    setSessions([]);
  };

  const handleMenuOpen = (event, session) => {
    setAnchorEl(event.currentTarget);
    setSelectedSession(session);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedSession(null);
  };

  const handleActionViewDetails = () => {
    if (selectedSession) {
      setSessionToView(selectedSession);
      setViewModalOpen(true);
    }
    handleMenuClose();
  };

  const handleReturnToExam = async (sessionItem) => {
    const item = sessionItem || selectedSession;
    if (!item) return;
    const sessionId = item.id || item.applicationSessionId;

    try {
      const response = await fetch("/api/support/return-applicant-to-exam", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: sessionId }),
      });

      const result = await response.json().catch(() => ({}));

      if (response.ok) {
        setSnackbarMessage(
          result.message || "Applicant returned to the exam successfully"
        );
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        const matchKey = (s) => (s.id ? s.id === item.id : s.applicationId === item.applicationId);
        setSessions((prev) =>
          prev.map((s) =>
            matchKey(s)
              ? {
                  ...s,
                  isSessionActive: false,
                  sessionKey: null,
                  isSubmitted: false,
                  submittedOn: null,
                  score: null,
                }
              : s
          )
        );

        if (sessionToView && matchKey(sessionToView)) {
          setSessionToView((prev) => ({
            ...prev,
            isSessionActive: false,
            sessionKey: null,
            isSubmitted: false,
            submittedOn: null,
            score: null,
          }));
        }
      } else {
        setSnackbarMessage(
          result.message || "Failed to return applicant to the exam."
        );
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error("Error returning applicant to exam:", err);
      setSnackbarMessage("An error occurred while updating the session.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      handleMenuClose();
    }
  };

  const handleRemoveApplicantCheat = async (sessionItem) => {
    const item = sessionItem || selectedSession;
    if (!item) return;
    const sessionId = item.id || item.applicationSessionId;
    try {
      const response = await fetch("/api/support/remove-applicant-cheat", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: sessionId }),
      });

      const result = await response.json().catch(() => ({}));

      if (response.ok) {
        setSnackbarMessage("Cheat flag successfully removed!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        const matchKey = (s) => (s.id ? s.id === item.id : s.applicationId === item.applicationId);

        setSessions((prev) =>
          prev.map((s) =>
            matchKey(s)
              ? {
                  ...s,
                  isMarkedAsCheated: false,
                  markedAsCheatedReason: null,
                }
              : s
          )
        );

        if (sessionToView && matchKey(sessionToView)) {
          setSessionToView((prev) => ({
            ...prev,
            isMarkedAsCheated: false,
            markedAsCheatedReason: null,
          }));
        }
      } else {
        setSnackbarMessage(
          result.message || "Failed to remove cheating flag."
        );
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error("Error removing cheat flag:", err);
      setSnackbarMessage("An error occurred while removing cheat flag.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      handleMenuClose();
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const DetailField = ({ label, value, icon: Icon }) => (
    <Box sx={{ display: "flex", alignItems: "flex-start", mb: 3 }}>
      {Icon && (
        <Box
          sx={{
            mr: 2,
            p: 1.2,
            borderRadius: 2,
            backgroundColor: alpha(SECONDARY_COLOR, 0.08),
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Icon fontSize="small" sx={{ color: SECONDARY_COLOR }} />
        </Box>
      )}
      <Box sx={{ flex: 1, overflow: "hidden" }}>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            display: "block",
            mb: 0.3,
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: PRIMARY_COLOR,
            wordWrap: "break-word",
            fontSize: "0.95rem",
          }}
        >
          {value !== undefined && value !== null && value !== "" ? value : "N/A"}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "16px",
        p: { xs: 2, sm: 3 },
        mb: 3,
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
        minHeight: "75vh",
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "center" },
          mb: 4,
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: PRIMARY_COLOR }}>
          Applicant Exam Session Management
        </Typography>
      </Box>

      {/* SEARCH CARD */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 3,
          border: `1px solid ${alpha(PRIMARY_COLOR, 0.1)}`,
          bgcolor: alpha(PRIMARY_COLOR, 0.015),
        }}
      >
        <Typography
          variant="h6"
          sx={{ mb: 1, color: PRIMARY_COLOR, fontWeight: 600 }}
        >
          Search Applicant Session
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
          Enter an Application ID to verify session progress, re-admit an applicant into the exam, or remove cheat flags.
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
            mb: 3,
          }}
        >
          <TextField
            placeholder="Enter Application ID"
            size="small"
            value={searchTerm}
            onChange={(e) => {
              const v = e.target.value;
              setSearchTerm(v);
              if (!v.trim()) setShowData(false);
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {searchTerm && (
                    <IconButton
                      aria-label="clear"
                      size="small"
                      onClick={handleClearSearch}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
            sx={{
              flexGrow: 1,
              maxWidth: 500,
              bgcolor: "white",
              borderRadius: 1,
            }}
          />
          <Button
            variant="contained"
            disableElevation
            startIcon={
              loading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <SearchIcon />
              )
            }
            onClick={handleSearch}
            disabled={loading}
            sx={{
              textTransform: "none",
              px: 4,
              py: 1,
              bgcolor: PRIMARY_COLOR,
              "&:hover": { bgcolor: SECONDARY_COLOR },
            }}
          >
            {loading ? "Searching..." : "Search"}
          </Button>
        </Box>

        <Collapse in={showData} timeout="auto">
          {error ? (
            <Typography
              color="error"
              sx={{ fontSize: "0.95rem", fontWeight: 500 }}
            >
              {error}
            </Typography>
          ) : sessions && sessions.length > 0 ? (
            <Box>
              <Typography
                sx={{ fontWeight: 600, color: PRIMARY_COLOR, mb: 1.5 }}
              >
                Found {sessions.length} Session
                {sessions.length > 1 ? "s" : ""}
              </Typography>
              <ResponsiveTableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, bgcolor: alpha(PRIMARY_COLOR, 0.08) }}>
                        Applicant ID
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: alpha(PRIMARY_COLOR, 0.08) }}>
                        Applicant Name
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: alpha(PRIMARY_COLOR, 0.08) }}>
                        Position
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, bgcolor: alpha(PRIMARY_COLOR, 0.08) }}>
                        Score
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, bgcolor: alpha(PRIMARY_COLOR, 0.08) }}>
                        Status
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, bgcolor: alpha(PRIMARY_COLOR, 0.08) }}>
                        Cheated?
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, bgcolor: alpha(PRIMARY_COLOR, 0.08) }}>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sessions.map((s, index) => (
                      <TableRow key={s.id || s.applicationId || index} hover>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {s.applicantId ?? "-"}
                        </TableCell>
                        <TableCell>{s.applicantName ?? "-"}</TableCell>
                        <TableCell>{s.position ?? "-"}</TableCell>
                        <TableCell align="center">
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color:
                                s.score !== null && s.score !== undefined
                                  ? s.score >= 50
                                    ? "success.main"
                                    : "error.main"
                                  : "text.secondary",
                            }}
                          >
                            {s.score !== null && s.score !== undefined ? `${s.score}%` : "-"}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={
                              s.isSessionActive
                                ? "Active"
                                : s.isSubmitted
                                ? "Submitted"
                                : "Inactive"
                            }
                            size="small"
                            color={
                              s.isSessionActive
                                ? "success"
                                : s.isSubmitted
                                ? "info"
                                : "default"
                            }
                            sx={{ fontWeight: 600, borderRadius: 2 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={s.isMarkedAsCheated ? "Cheated" : "Clean"}
                            size="small"
                            color={s.isMarkedAsCheated ? "error" : "success"}
                            variant={s.isMarkedAsCheated ? "filled" : "outlined"}
                            sx={{ fontWeight: 600, borderRadius: 2 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            onClick={(e) => handleMenuOpen(e, s)}
                            size="small"
                            sx={{
                              color: PRIMARY_COLOR,
                              backgroundColor: alpha(PRIMARY_COLOR, 0.05),
                              "&:hover": {
                                backgroundColor: alpha(PRIMARY_COLOR, 0.15),
                              },
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ResponsiveTableContainer>

              <Menu
                anchorEl={anchorEl}
                open={actionMenuOpen}
                onClose={handleMenuClose}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    overflow: "visible",
                    filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.15))",
                    mt: 1,
                    minWidth: 210,
                    borderRadius: 2,
                    "& .MuiMenuItem-root": {
                      px: 2,
                      py: 1.2,
                      "&:hover": {
                        backgroundColor: alpha(PRIMARY_COLOR, 0.04),
                      },
                    },
                  },
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                <MenuItem onClick={handleActionViewDetails}>
                  <ListItemIcon>
                    <VisibilityIcon fontSize="small" sx={{ color: SECONDARY_COLOR }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="View Details"
                    primaryTypographyProps={{
                      fontWeight: 500,
                      fontSize: "0.9rem",
                    }}
                  />
                </MenuItem>

                {/* Return Applicant to Exam */}
                <MenuItem onClick={() => handleReturnToExam(selectedSession)}>
                  <ListItemIcon>
                    <ReplayIcon fontSize="small" sx={{ color: SECONDARY_COLOR }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Return to Exam"
                    primaryTypographyProps={{
                      fontWeight: 500,
                      fontSize: "0.9rem",
                    }}
                  />
                </MenuItem>

                {/* Remove Cheat */}
                <MenuItem
                  onClick={() => handleRemoveApplicantCheat(selectedSession)}
                  disabled={!selectedSession?.isMarkedAsCheated}
                >
                  <ListItemIcon>
                    <CheckCircleOutlineIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="UnMark As Cheat"
                    primaryTypographyProps={{
                      fontWeight: 500,
                      fontSize: "0.9rem",
                    }}
                  />
                </MenuItem>
              </Menu>
            </Box>
          ) : (
            <Typography sx={{ color: "text.secondary" }}>
              No results found.
            </Typography>
          )}
        </Collapse>
      </Paper>

      {/* VIEW DETAILS MODAL */}
      <Dialog
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        maxWidth="lg"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "16px",
            backgroundColor: "#f8fafc",
          },
          backgroundColor: "rgba(0, 0, 0, 0.6)",
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
            backgroundColor: "#ffffff",
            borderBottom: `1px solid ${alpha(PRIMARY_COLOR, 0.08)}`,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 800, color: PRIMARY_COLOR }}>
            Applicant Exam Session Details
          </Typography>
          <IconButton
            onClick={() => setViewModalOpen(false)}
            size="small"
            sx={{
              color: "text.secondary",
              "&:hover": {
                color: PRIMARY_COLOR,
                bgcolor: alpha(PRIMARY_COLOR, 0.1),
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 2, md: 4 } }}>
          {sessionToView && (
            <Box>
              {/* Profile Header Widget */}
              <Paper
                elevation={0}
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "flex-start", sm: "center" },
                  p: 3,
                  mb: 4,
                  borderRadius: 4,
                  border: `1px solid ${alpha(SECONDARY_COLOR, 0.15)}`,
                  background: `linear-gradient(135deg, ${DEFAULT_COLOR} 0%, ${alpha(SECONDARY_COLOR, 0.05)} 100%)`,
                }}
              >
                <Avatar
                  sx={{
                    width: 86,
                    height: 86,
                    bgcolor: PRIMARY_COLOR,
                    fontSize: "2rem",
                    fontWeight: 700,
                    mb: { xs: 2, sm: 0 },
                    mr: { sm: 3 },
                    boxShadow: `0 4px 14px ${alpha(PRIMARY_COLOR, 0.3)}`,
                  }}
                >
                  <PersonOutlineIcon sx={{ fontSize: "2.5rem" }} />
                </Avatar>

                <Box sx={{ flexGrow: 1 }}>
                  {/* Displays applicant name or fallback */}
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 800, color: PRIMARY_COLOR, mb: 0.5 }}
                  >
                    {sessionToView.applicantName || `Applicant #${sessionToView.applicantId}`}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{ color: SECONDARY_COLOR, fontWeight: 600, mb: 1.5 }}
                  >
                    Application ID: {sessionToView.applicationId}
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    <Chip
                      icon={<VpnKeyIcon fontSize="small" />}
                      label={sessionToView.sessionKey ? `Key: ${sessionToView.sessionKey}` : "No Session Key"}
                      color="primary"
                      sx={{ fontWeight: 600, borderRadius: 2 }}
                    />

                    <Chip
                      label={sessionToView.isSessionActive ? "Active Session" : "Inactive Session"}
                      color={sessionToView.isSessionActive ? "success" : "default"}
                      size="small"
                      sx={{ fontWeight: 600, borderRadius: 2 }}
                    />

                    <Chip
                      label={sessionToView.isSubmitted ? "Exam Submitted" : "Pending Submission"}
                      color={sessionToView.isSubmitted ? "info" : "warning"}
                      size="small"
                      sx={{ fontWeight: 600, borderRadius: 2 }}
                    />

                    {sessionToView.isMarkedAsCheated && (
                      <Chip
                        icon={<ReportProblemOutlinedIcon fontSize="small" />}
                        label="Cheating Flagged"
                        color="error"
                        size="small"
                        sx={{ fontWeight: 700, borderRadius: 2 }}
                      />
                    )}

                    {/* Return to exam button */}
                    <Button
                      variant="contained"
                      startIcon={<ReplayIcon />}
                      onClick={() => handleReturnToExam(sessionToView)}
                      sx={{
                        bgcolor: SECONDARY_COLOR,
                        "&:hover": { bgcolor: PRIMARY_COLOR },
                        fontWeight: 600,
                        px: 2.5,
                        py: 0.8,
                        borderRadius: 2,
                        textTransform: "none",
                      }}
                    >
                      Return to Exam
                    </Button>

                    {/* Unmark as cheat button */}
                    {sessionToView.isMarkedAsCheated && (
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircleOutlineIcon />}
                        onClick={() => handleRemoveApplicantCheat(sessionToView)}
                        sx={{
                          fontWeight: 600,
                          px: 2.5,
                          py: 0.8,
                          borderRadius: 2,
                          textTransform: "none",
                        }}
                      >
                        Unmark As Cheat
                      </Button>
                    )}
                  </Box>
                </Box>
              </Paper>

              <Grid container spacing={4}>
                {/* General Session Info */}
                <Grid item xs={12} md={6}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 4,
                      border: `1px solid ${alpha(PRIMARY_COLOR, 0.08)}`,
                      height: "100%",
                      boxShadow: "0px 4px 20px rgba(0,0,0,0.02)",
                    }}
                  >
                    <CardContent sx={{ p: 3, pt: 4 }}>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                        <PersonOutlineIcon sx={{ color: SECONDARY_COLOR, mr: 1 }} />
                        <Typography variant="h6" sx={{ color: PRIMARY_COLOR, fontWeight: 700 }}>
                          Session Metadata
                        </Typography>
                      </Box>
                      <Divider sx={{ mb: 3, borderColor: alpha(PRIMARY_COLOR, 0.05) }} />

                      {/* Added Applicant Name here */}
                      <DetailField
                        icon={PersonOutlineIcon}
                        label="Applicant Name"
                        value={sessionToView.applicantName}
                      />
                      <DetailField
                        icon={BadgeIcon}
                        label="Applicant ID"
                        value={sessionToView.applicantId}
                      />
                      <DetailField
                        icon={WorkOutlineIcon}
                        label="Position"
                        value={sessionToView.position}
                      />
                      <DetailField
                        icon={BadgeIcon}
                        label="Application ID"
                        value={sessionToView.applicationId}
                      />
                      <DetailField
                        icon={VpnKeyIcon}
                        label="Session Key"
                        value={sessionToView.sessionKey || "None (Cleared)"}
                      />
                      <DetailField
                        icon={CalendarMonthIcon}
                        label="Exam Started On"
                        value={formatDateTime(sessionToView.startedExamOn)}
                      />
                      <DetailField
                        icon={CalendarMonthIcon}
                        label="Exam Submitted On"
                        value={formatDateTime(sessionToView.submittedOn)}
                      />
                    </CardContent>
                  </Card>
                </Grid>

                {/* Score & Integrity Verification */}
                <Grid item xs={12} md={6}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 4,
                      border: `1px solid ${alpha(PRIMARY_COLOR, 0.08)}`,
                      height: "100%",
                      boxShadow: "0px 4px 20px rgba(0,0,0,0.02)",
                    }}
                  >
                    <CardContent sx={{ p: 3, pt: 4 }}>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                        <AssessmentIcon sx={{ color: SECONDARY_COLOR, mr: 1 }} />
                        <Typography variant="h6" sx={{ color: PRIMARY_COLOR, fontWeight: 700 }}>
                          Exam Score & Integrity
                        </Typography>
                      </Box>
                      <Divider sx={{ mb: 3, borderColor: alpha(PRIMARY_COLOR, 0.05) }} />

                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <DetailField
                            icon={FactCheckIcon}
                            label="Session Status"
                            value={sessionToView.isSessionActive ? "Active" : "Inactive"}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <DetailField
                            icon={FactCheckIcon}
                            label="Submission Status"
                            value={sessionToView.isSubmitted ? "Submitted" : "Pending"}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <DetailField
                            icon={WarningAmberIcon}
                            label="Cheating Flag"
                            value={sessionToView.isMarkedAsCheated ? "Marked as Cheated" : "Clean"}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <DetailField
                            icon={FactCheckIcon}
                            label="Cheating Reason"
                            value={sessionToView.markedAsCheatedReason || "None reported"}
                          />
                        </Grid>
                      </Grid>

                      <Divider sx={{ my: 2, borderColor: alpha(PRIMARY_COLOR, 0.05) }} />

                      <Box
                        sx={{
                          p: 2.5,
                          borderRadius: 2,
                          bgcolor: alpha(SECONDARY_COLOR, 0.05),
                          border: `1px dashed ${alpha(SECONDARY_COLOR, 0.3)}`,
                          textAlign: "center",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Exam Final Score
                        </Typography>
                        <Typography
                          variant="h3"
                          sx={{
                            mt: 1,
                            color:
                              sessionToView.score !== null && sessionToView.score !== undefined
                                ? sessionToView.score >= 50
                                  ? theme.palette.success.main
                                  : theme.palette.error.main
                                : theme.palette.text.secondary,
                            fontWeight: 800,
                          }}
                        >
                          {sessionToView.score !== null && sessionToView.score !== undefined
                            ? `${sessionToView.score}%`
                            : "N/A"}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, backgroundColor: "#ffffff" }}>
          <Button
            onClick={() => setViewModalOpen(false)}
            variant="contained"
            disableElevation
            sx={{
              fontWeight: 600,
              px: 4,
              py: 1,
              borderRadius: 2,
              backgroundColor: PRIMARY_COLOR,
              "&:hover": { backgroundColor: SECONDARY_COLOR },
            }}
          >
            Close Details
          </Button>
        </DialogActions>
      </Dialog>

      <NotificationSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Box>
  );
}

export default ApplicantSession;